import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  BarChart3, Clock, AlertCircle, Settings, GitBranch,
  SlidersHorizontal, AlertTriangle, Calendar,
  Users, ArrowUpFromLine, Network, Lock, UserCog,
  Cog, LogOut, BookOpen, Database, Sun, Moon, ClipboardCheck, FolderTree,
  Search, User, Ticket, Inbox, X, ClipboardList
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

// Role constants
const R_ALL = ['admin', 'it_manager', 'approver', 'it_support', 'end_user'];
const R_END_USER = ['end_user'];
const R_SUPPORT = ['admin', 'it_manager', 'approver', 'it_support'];
const R_MANAGER = ['admin', 'it_manager'];
const R_ADMIN = ['admin'];
const R_APPROVER = ['admin', 'it_manager', 'approver'];

const navSections = [
  {
    label: 'Main', roles: R_ALL,
    items: [
      { to: '/', icon: BarChart3, label: 'Dashboard', color: 'text-indigo-600', roles: R_ALL },
      { to: '/my-sla', icon: Clock, label: 'My SLA Dashboard', color: 'text-indigo-500', roles: R_ALL },
      { to: '/my-approvals', icon: ClipboardCheck, label: 'My Approvals', color: 'text-amber-500', roles: R_APPROVER },
    ],
  },
  {
    label: 'Tickets', roles: R_ALL,
    items: [
      { to: '/incidents', icon: AlertCircle, label: 'Incidents', color: 'text-rose-500', roles: R_ALL },
      { to: '/service-requests', icon: Settings, label: 'Service Requests', color: 'text-blue-500', roles: R_ALL },
      { to: '/change-requests', icon: GitBranch, label: 'Change Requests', color: 'text-purple-500', roles: R_SUPPORT },
      { to: '/my-tickets-search', icon: Search, label: 'Search Tickets', color: 'text-sky-500', roles: R_ALL },
    ],
  },
  {
    label: 'My Work', roles: R_SUPPORT,
    items: [
      { to: '/my-tickets', icon: Ticket, label: 'My Tickets', color: 'text-sky-500', roles: R_SUPPORT },
      { to: '/my-bucket', icon: Inbox, label: 'My Bucket', color: 'text-violet-500', roles: R_SUPPORT },
      { to: '/delegations', icon: ArrowUpFromLine, label: 'Delegations', color: 'text-amber-500', roles: R_APPROVER },
    ],
  },
  {
    label: 'SLA & Reports', roles: R_SUPPORT,
    items: [
      { to: '/sla-policies', icon: SlidersHorizontal, label: 'SLA Policies', color: 'text-amber-500', roles: R_MANAGER },
      { to: '/breach-report', icon: AlertTriangle, label: 'SLA Breach Report', color: 'text-rose-500', roles: R_SUPPORT },
    ],
  },
  {
    label: 'Assignment', roles: R_SUPPORT,
    items: [
      { to: '/shift-calendar', icon: Calendar, label: 'Shift Calendar', color: 'text-teal-500', roles: R_SUPPORT },
      { to: '/teams', icon: Users, label: 'Apps & Teams Mapping', color: 'text-sky-500', roles: R_SUPPORT },
      { to: '/escalation', icon: ArrowUpFromLine, label: 'Escalation Matrix', color: 'text-orange-500', roles: R_MANAGER },
    ],
  },
  {
    label: 'Resources', roles: R_ALL,
    items: [
      { to: '/knowledge-base', icon: BookOpen, label: 'Knowledge Base', color: 'text-indigo-500', roles: R_ALL },
      { to: '/cmdb', icon: Database, label: 'CMDB', color: 'text-cyan-500', roles: R_SUPPORT },
    ],
  },
  {
    label: 'Admin', roles: R_MANAGER,
    items: [
      { to: '/app-owners', icon: Network, label: 'Applications / Systems', color: 'text-cyan-500', roles: R_MANAGER },
      { to: '/categories', icon: FolderTree, label: 'Categories', color: 'text-sky-500', roles: R_MANAGER },
      { to: '/audit-logs', icon: ClipboardList, label: 'Audit Logs', color: 'text-indigo-500', roles: R_MANAGER },
      { to: '/authentication', icon: Lock, label: 'Authentication', color: 'text-indigo-500', roles: R_ADMIN },
      { to: '/user-roles', icon: UserCog, label: 'User Roles', color: 'text-slate-500', roles: R_ADMIN },
      { to: '/system-config', icon: Cog, label: 'System Config', color: 'text-slate-500', roles: R_MANAGER },
    ],
  },
  {
    label: 'Help', roles: R_ALL,
    items: [
      { to: '/help', icon: BookOpen, label: 'Help Documentation', color: 'text-amber-500', roles: R_ALL },
    ],
  },
];

export default function Sidebar({ onClose }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const userRole = (user?.role_name || user?.role?.name || 'end_user').toLowerCase().replace(/\s+/g, '_');
  const logoUrl = user?.tenant_logo_url || null;
  const appName = user?.tenant_app_name || 'SupportOne';
  const tenantName = user?.tenant?.name || 'TRIVONT';

  return (
    <aside className="w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 flex flex-col h-screen" data-testid="sidebar">
      {/* Logo + mobile close */}
      <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
        <div className="flex flex-col gap-1 min-w-0">
          {logoUrl ? (
            /* Wide banner logo — max 120×48px, preserves aspect ratio */
            <img
              src={logoUrl}
              alt={tenantName}
              className="h-10 w-auto max-w-[144px] object-contain rounded"
              style={{ maxHeight: '40px' }}
            />
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-sky-500 rounded-xl flex items-center justify-center font-black text-white text-sm tracking-tight flex-shrink-0">
                <span>{tenantName.charAt(0).toUpperCase()}</span>
              </div>
              <span className="font-bold text-slate-800 dark:text-white tracking-tight truncate">{tenantName}</span>
            </div>
          )}
          <span className="text-xs text-slate-500 dark:text-slate-400 truncate">{appName}</span>
        </div>
        {/* Close button — only visible on mobile */}
        {onClose && (
          <button onClick={onClose} className="lg:hidden p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navSections.filter(section => section.roles.includes(userRole)).map((section) => {
          const visibleItems = section.items.filter(item => item.roles.includes(userRole));
          if (visibleItems.length === 0) return null;
          return (
          <div key={section.label}>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 py-2 mt-3 first:mt-0">
              {section.label}
            </div>
            {visibleItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.to;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onClose}
                  data-testid={`nav-${item.to.replace(/\//g, '').replace(/-/g, '-') || 'dashboard'}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all text-sm font-medium ${
                    isActive
                      ? 'bg-gradient-to-r from-sky-600 to-sky-500 text-white shadow-md'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-white' : item.color}`} />
                  <span>{item.label}</span>
                  {item.badge && (
                    <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${
                      isActive ? 'bg-white/20 text-white' : item.badgeColor
                    }`}>
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
          );
        })}
      </nav>

      {/* User Profile */}
      <div className="p-4 border-t border-slate-100 dark:border-slate-700">
        <div className="flex items-center gap-2 mb-3">
          <button onClick={toggleTheme} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors" data-testid="theme-toggle">
            {theme === 'dark' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            {theme === 'dark' ? 'Light' : 'Dark'} Mode
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/profile')} className="w-9 h-9 bg-sky-100 dark:bg-sky-900 rounded-full flex items-center justify-center hover:bg-sky-200 dark:hover:bg-sky-800 transition-colors" title="My Profile" data-testid="profile-avatar">
            <User className="w-4 h-4 text-sky-600" />
          </button>
          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate('/profile')}>
            <span className="text-sm font-medium text-slate-800 dark:text-white block truncate hover:text-sky-600 transition-colors" data-testid="user-name">
              {user?.first_name || 'Admin'} {user?.last_name || 'User'}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400">{user?.role_name || 'Admin'}</span>
          </div>
          <button
            onClick={logout}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
